import UPSnackBar from 'components/app-container/snackbar';
import DeleteModal from 'components/delete-modal';
import MessageModal from 'components/message-modal';
import { CompanyRole } from 'constants/CompanyRole';
import { OnboardingCompanyProgress } from 'constants/OnboardingCompanyProgress';
import { toNumber } from 'lodash';
import { getCurrentTime, getISO8601WeekNumber, getISO8601Year } from 'modules/contracts/helpers';
import AddContractModal from 'modules/contracts/modals/add/AddContractModal';
import ConfirmContractModal from 'modules/contracts/modals/ConfirmContractModal';
import EditContractModal from 'modules/contracts/modals/edit/EditContractModal';
import { IContractToEdit, IContractsCountPerWeek } from 'modules/contracts/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { getDashboardBlogsAction } from 'store/actions/blog-actions';
import { getDefaultCompanyBreakTimeAction } from 'store/actions/company-actions';
import { getCostCentersAction, getCostCentersDropdownItemsAction } from 'store/actions/company-cost-centers-actions';
import { getFunctionsAsDropdownItemsAction } from 'store/actions/company-functions-actions';
import { getLocationsDropdownItemsAction } from 'store/actions/company-locations-actions';
import {
  deleteContractDashboardAction,
  getContractByIdAction,
  getContractCardsOfAdjacentWeeksAction,
  getContractsCountFromPreviousWeeksAction,
  getCostEstimationAction,
  reopenConfirmationAction,
  validateContractErrorMessageAction,
  validateContractErrorMessageExtraInfoAction,
  validateContractErrorMessageRowAction,
  validateContractSuccessAction,
} from 'store/actions/contract-actions';
import { getEmployeesCountAction, setEmployeeError } from 'store/actions/employee-actions';
import { companyOnboardingProgressSelector } from 'store/selectors/company-onboarding-selectors';
import { defaultCompanyBreakSelector } from 'store/selectors/company-selectors';
import { getContractsCountFromPreviousWeeksSelector } from 'store/selectors/contracts-selector';
import { employeesCountSelector, employeesErrorSelector } from 'store/selectors/employee-selector';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  CONTRACTS__ALREADY_CONFIRMED,
  CONTRACTS__ARE_YOU_SURE,
  CONTRACTS__DEFAULT_ERROR,
  CONTRACTS__DELETE_WEEKLY_CONTRACT_WARNING,
  EMPLOYEES__IBAN_ERROR,
  EMPLOYEES__INSS_ERROR,
  EMPLOYEES__PERMANENT_CHANGE_VALIDATION,
  GENERAL__CANCEL_BUTTON,
} from 'translations/constants';
import { IContractCard } from 'types/contract/IContractCard';
import { IEmployeeTotals } from 'types/employee';
import { filterContractsByDate, getAdjacentDay } from './helpers';
import {
  BlogContainer,
  BlogsList,
  ContractsContainer,
  ContractsListParent,
  ContractsOverview,
  CostEstimationContainer,
  DashboardContainer,
  DashboardRightSide,
  DashboardTilesContainer,
  EmployeeContainer,
  OldEmployeeContainer,
  ShortcutsContainer,
} from './styles';
import DashboardBlogsList from './tiles/blogs/DashboardBlogsList';
import DashboardContractsGraph from './tiles/contracts/DashboardContractsGraph';
import DashboardContractsTile from './tiles/contracts/DashboardContractsTile';
import DashboardCostEstimationTile from './tiles/cost-estimation/DashboardCostEstimationTile';
import DashboardEmployeeCard from './tiles/employees/DashboardEmployeeCard';
import DashboardEmployeeTile from './tiles/employees/DashboardEmployeeTile';
import DashboardShortcutsList from './tiles/shortcuts/DashboardShortcutsList';
import { ContractsFilteredByDays } from './types';
import AdminDashboard from './users/admin/AdminDashboard';
import EmployeeDashboard from './users/employee/EmployeeDashboard';
import OnboardingManagerDashboard from './users/onboarding-manager/OnboardingManagerDashboard';

const Dashboard = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();

  const profile = useAppSelector(profileSelector);
  const contractsCountFromPreviousWeeks = useAppSelector(getContractsCountFromPreviousWeeksSelector);
  const onboardingCompanyProgress = useAppSelector(companyOnboardingProgressSelector);
  const employeesNumbers = useAppSelector(employeesCountSelector);
  const userCompanyId = profile.companyId;
  const onboardingCompanyIdNumber =
    !profile.companyId && profile.role === CompanyRole.COMPANY_MANAGER ? +profile.currentCompany : null;
  const userRole = profile.role;
  const employeeError = useAppSelector(employeesErrorSelector);
  const defaultBreakTime = useAppSelector(defaultCompanyBreakSelector);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [location, setLocation] = useState<string>('');
  const [currentWeek, setCurrentWeek] = useState<number>(getISO8601WeekNumber(new Date(Date.now())));
  const [isViewOnlyMode, setIsViewOnlyMode] = useState(false);
  const [filteredContracts, setFilteredContracts] = useState<ContractsFilteredByDays>(null);
  const [currentYear, setCurrentYear] = useState<number>(getISO8601Year(new Date(Date.now())));
  const [contractsNumber, setContractsNumber] = useState<IContractsCountPerWeek[]>(null);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>();
  const [openAlreadyConfirmed, setOpenAlreadyConfirmed] = useState<boolean>();
  const [cardToConfirmId, setCardToConfirmId] = useState<number | null>(null);
  const [employeesCount, setEmployeesCount] = useState<IEmployeeTotals>(null);
  const [currentDate, setCurrentDate] = useState<Date>(getCurrentTime().date);
  const [selectedContractsIds, setSelectedContractsIds] = useState<number[]>([]);
  const [openConfirmDeleteWeeklyModal, setOpenConfirmDeleteWeeklyModal] = useState<boolean>(false);
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [cardToEdit, setCardToEdit] = useState<IContractToEdit>(null);
  const [openConfirmCloseModal, setOpenConfirmCloseModal] = useState<boolean>(false);
  const [openEmployeeError, setOpenEmployeeError] = useState<boolean>(false);
  const [contractCards, setContractCards] = useState<IContractCard[]>([]);

  const companyId = userCompanyId !== null ? userCompanyId.toString() : undefined;
  const isEmployee = userRole === CompanyRole.EMPLOYEE;
  const hasAccessToNewDashboard = userRole === CompanyRole.COMPANY_MANAGER || userRole === CompanyRole.HR_MANAGER;

  const employeeErrorMessages = useMemo(() => {
    return {
      default: t(CONTRACTS__DEFAULT_ERROR),
      inss: t(EMPLOYEES__INSS_ERROR),
      iban: t(EMPLOYEES__IBAN_ERROR),
      permanent: t(EMPLOYEES__PERMANENT_CHANGE_VALIDATION),
    };
  }, [t]);

  const fetchContractCards = useCallback(async () => {
    if (!companyId || toNumber(companyId) === 0) return;

    try {
      const result = await dispatch(
        getContractCardsOfAdjacentWeeksAction({
          companyId: +companyId,
          year: currentYear,
          week: currentWeek,
          locationId: +location,
        }),
      );

      if (getContractCardsOfAdjacentWeeksAction.fulfilled.match(result)) {
        setContractCards(result.payload);
      }
    } catch (error) {
      console.error('Error fetching contract cards:', error);
    }
  }, [companyId, dispatch, currentWeek, currentYear, location]);

  const onClickConfirm = (cardId: number) => {
    const cardToConfirm = contractCards.find(card => card.contractDateId === cardId);
    if (cardToConfirm.isConfirmed) {
      setOpenAlreadyConfirmed(true);
    } else {
      setCardToConfirmId(cardToConfirm.contractDateId);
      setOpenConfirmModal(true);
    }
  };

  const makeDeleteRequest = (ids: number[]) => {
    dispatch(
      deleteContractDashboardAction({
        companyId: toNumber(companyId),
        contractsIds: ids,
        year: currentYear,
        weekNumber: currentWeek,
      }),
    ).then(() => {
      fetchContractCards();
    });
  };

  const onDeleteCheckedContracts = (ids: number[]) => {
    const newContracts = contractCards.filter(card => ids.includes(card.contractDateId));
    setSelectedContractsIds(ids);
    if (newContracts.some(card => card.isWeekly)) setOpenConfirmDeleteWeeklyModal(true);
    else {
      setOpenConfirmDeleteModal(true);
    }
  };

  const onAcceptClosingEditModal = () => {
    setOpenEditModal(false);
    setOpenConfirmCloseModal(false);
    dispatch(validateContractSuccessAction(false));
    dispatch(validateContractErrorMessageAction(''));
    dispatch(validateContractErrorMessageExtraInfoAction(''));
    dispatch(validateContractErrorMessageRowAction(null));
  };

  const onClickEditCard = (id: number, isViewOnly = false) => {
    setIsViewOnlyMode(isViewOnly);
    dispatch(
      getContractByIdAction({
        companyId: toNumber(companyId),
        contractId: id,
      }),
    )
      .unwrap()
      .then(contract => {
        setCardToEdit(contract);
        setOpenEditModal(true);
      });
  };

  const onClickReopenConfirmation = (contractDateId: number) => {
    dispatch(
      reopenConfirmationAction({
        companyId: toNumber(companyId),
        contractDateId,
        year: currentYear,
        weekNumber: currentWeek,
      }),
    ).then(() => {
      fetchContractCards();
    });
  };

  useEffect(() => {
    if (employeeError) setOpenEmployeeError(true);
  }, [employeeError]);

  useEffect(() => {
    const auxContracts: ContractsFilteredByDays = {
      previousDayContracts: filterContractsByDate(contractCards, getAdjacentDay(currentDate, -1)),
      currentDayContracts: filterContractsByDate(contractCards, currentDate),
      nextDayContracts: filterContractsByDate(contractCards, getAdjacentDay(currentDate, 1)),
    };
    setFilteredContracts(auxContracts);
  }, [contractCards, currentDate]);

  useEffect(() => {
    setCurrentYear(getISO8601Year(currentDate));
    setCurrentWeek(getISO8601WeekNumber(currentDate));
  }, [currentDate]);

  useEffect(() => {
    if (contractsCountFromPreviousWeeks) {
      setContractsNumber(contractsCountFromPreviousWeeks);
    }
  }, [contractsCountFromPreviousWeeks, contractsNumber]);

  useEffect(() => {
    if (employeesNumbers && employeesCount === null) {
      setEmployeesCount(employeesNumbers);
    }
  }, [employeesCount, employeesNumbers]);

  useEffect(() => {
    if (companyId && !isEmployee && onboardingCompanyProgress === OnboardingCompanyProgress.COMPLETED) {
      dispatch(getCostCentersDropdownItemsAction({ companyId: toNumber(companyId) }));
      dispatch(getCostCentersAction(+companyId));
      dispatch(getFunctionsAsDropdownItemsAction(toNumber(companyId)));
      dispatch(getLocationsDropdownItemsAction({ companyId: toNumber(companyId) }));
      dispatch(getDefaultCompanyBreakTimeAction(toNumber(companyId)));
      fetchContractCards();
      dispatch(
        getContractsCountFromPreviousWeeksAction({
          companyId: toNumber(companyId),
          year: currentYear,
          weekNumber: currentWeek,
          locationId: toNumber(location),
        }),
      );
      dispatch(
        getEmployeesCountAction({
          companyId: toNumber(companyId),
          weekNumber: currentWeek,
        }),
      );
      dispatch(getDashboardBlogsAction());
      dispatch(
        getCostEstimationAction({
          companyId: toNumber(companyId),
          weekNumber: currentWeek,
          year: currentYear,
        }),
      );
    }
    if (companyId && !isEmployee && onboardingCompanyProgress === OnboardingCompanyProgress.AWAITING_APPROVAL) {
      dispatch(
        getEmployeesCountAction({
          companyId: toNumber(companyId),
          weekNumber: currentWeek,
        }),
      );
    }
    if (isEmployee) {
      fetchContractCards();
      dispatch(
        getContractsCountFromPreviousWeeksAction({
          year: currentYear,
          weekNumber: currentWeek,
          locationId: 0,
        }),
      );
      dispatch(getCostCentersAction(+companyId));
      dispatch(getCostCentersDropdownItemsAction({ companyId: +companyId }));

      dispatch(getDashboardBlogsAction());
    }
  }, [
    companyId,
    currentYear,
    currentWeek,
    dispatch,
    isEmployee,
    location,
    onboardingCompanyProgress,
    fetchContractCards,
  ]);

  if (isEmployee) {
    return (
      <EmployeeDashboard
        setOpenAddModal={setOpenAddModal}
        contractsNumber={contractsNumber}
        cardToView={cardToEdit}
        openEditModal={openEditModal}
        setOpenEditModal={setOpenEditModal}
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        onClickEditCard={onClickEditCard}
        filteredContracts={filteredContracts}
        year={currentYear}
        week={currentWeek}
      />
    );
  }

  if (userCompanyId && onboardingCompanyProgress === OnboardingCompanyProgress.COMPLETED) {
    return (
      <DashboardContainer>
        <ContractsContainer>
          <ContractsOverview>
            <DashboardContractsGraph
              openAddNewContractModal={() => setOpenAddModal(true)}
              contractsNumber={contractsNumber}
              isEmployee={isEmployee}
            />
          </ContractsOverview>
          <ContractsListParent>
            <DashboardContractsTile
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              onDeleteCheckedContracts={onDeleteCheckedContracts}
              onClickEditCard={onClickEditCard}
              filteredContracts={filteredContracts}
              location={location}
              setLocation={setLocation}
              isEmployee={isEmployee}
              onClickConfirm={onClickConfirm}
              onReopen={onClickReopenConfirmation}
              year={currentYear}
              weekNumber={currentWeek}
            />
          </ContractsListParent>
        </ContractsContainer>
        <DashboardRightSide>
          {userCompanyId && hasAccessToNewDashboard ? (
            <DashboardTilesContainer>
              <EmployeeContainer>
                <DashboardEmployeeTile
                  employeesInvited={employeesCount ? employeesCount.invited : 0}
                  employeesReady={employeesCount ? employeesCount.ready : 0}
                  employeesDeployed={employeesCount ? employeesCount.deployed : 0}
                  companyId={userCompanyId}
                />
              </EmployeeContainer>
              <CostEstimationContainer>
                <DashboardCostEstimationTile />
              </CostEstimationContainer>
            </DashboardTilesContainer>
          ) : (
            userCompanyId &&
            !isEmployee && (
              <OldEmployeeContainer>
                <DashboardEmployeeCard
                  employeesInvited={employeesCount?.invited ?? 0}
                  employeesReady={employeesCount?.ready ?? 0}
                  employeesDeployed={employeesCount?.deployed ?? 0}
                  companyId={userCompanyId.toString()}
                />
              </OldEmployeeContainer>
            )
          )}

          {isEmployee && (
            <ShortcutsContainer>
              <DashboardShortcutsList />
            </ShortcutsContainer>
          )}
          <BlogContainer sx={{ width: '100%' }}>
            <BlogsList>
              <DashboardBlogsList />
            </BlogsList>
          </BlogContainer>
        </DashboardRightSide>
        {openAddModal && (
          <AddContractModal
            onClose={() => {
              setOpenAddModal(false);
            }}
            onSuccess={() => {
              // made the post req
            }}
            companyId={companyId}
            week={currentWeek}
            weekDate={currentDate}
            defaultBreakTime={defaultBreakTime}
          />
        )}
        {openConfirmDeleteWeeklyModal && (
          <DeleteModal
            open={openConfirmDeleteWeeklyModal}
            content={t(CONTRACTS__DELETE_WEEKLY_CONTRACT_WARNING)}
            onClose={() => {
              setOpenConfirmDeleteWeeklyModal(false);
            }}
            onDelete={() => {
              makeDeleteRequest(selectedContractsIds);
            }}
          />
        )}

        {openConfirmDeleteModal && (
          <DeleteModal
            open={openConfirmDeleteModal}
            content={t(CONTRACTS__ARE_YOU_SURE)}
            onClose={() => {
              setOpenConfirmDeleteModal(false);
            }}
            onDelete={() => {
              makeDeleteRequest(selectedContractsIds);
            }}
          />
        )}
        {openEditModal && (
          <EditContractModal
            onClose={() => {
              if (isEmployee) setOpenEditModal(false);
              else setOpenConfirmCloseModal(true);
            }}
            onSuccess={() => {
              fetchContractCards();
              setOpenEditModal(false);
              setOpenConfirmCloseModal(false);
            }}
            companyId={`${cardToEdit.companyId}`}
            week={currentWeek}
            contractToEdit={cardToEdit}
            weekDate={cardToEdit?.date}
            isContractSlot={false}
            defaultBreakTime={defaultBreakTime}
            isViewOnly={isViewOnlyMode}
          />
        )}

        {openConfirmModal && (
          <ConfirmContractModal
            open={openConfirmModal}
            onClose={() => {
              setOpenConfirmModal(false);
              fetchContractCards();
            }}
            companyId={companyId}
            contractDateId={cardToConfirmId}
            // contractError={cardToConfirm.errorMessage ? cardToConfirm.errorMessage : null}
            onSuccess={() => {
              fetchContractCards();
            }}
          />
        )}

        {openAlreadyConfirmed && (
          <MessageModal
            message={t(CONTRACTS__ALREADY_CONFIRMED)}
            buttonText={t(GENERAL__CANCEL_BUTTON)}
            open={openAlreadyConfirmed}
            onClose={() => setOpenAlreadyConfirmed(false)}
          />
        )}

        {openConfirmCloseModal && (
          <DeleteModal
            open={openConfirmCloseModal}
            content={t(CONTRACTS__ARE_YOU_SURE)}
            onClose={() => {
              setOpenConfirmCloseModal(false);
            }}
            onDelete={() => {
              onAcceptClosingEditModal();
            }}
          />
        )}
        {employeeError && (
          <UPSnackBar
            vertical="bottom"
            horizontal="right"
            open={openEmployeeError}
            content={
              employeeError === 'inss' ||
              employeeError === 'email' ||
              employeeError === 'iban' ||
              employeeError === 'default' ||
              employeeError === 'permanent'
                ? employeeErrorMessages[employeeError]
                : t(employeeError)
            }
            type="error"
            onClose={() => {
              setOpenEmployeeError(false);
              dispatch(setEmployeeError(null));
            }}
            index={0}
          />
        )}
      </DashboardContainer>
    );
  }

  if (onboardingCompanyIdNumber || onboardingCompanyProgress === OnboardingCompanyProgress.AWAITING_APPROVAL) {
    return (
      <OnboardingManagerDashboard
        companyIdNumber={onboardingCompanyIdNumber}
        companyId={+companyId}
        onboardingProgress={onboardingCompanyProgress}
        employeesCount={employeesCount}
      />
    );
  }

  if (userRole === CompanyRole.ACCOUNT_MANAGER) {
    return <div>Account manager dashboard</div>;
  }

  return userRole === CompanyRole.ADMIN ? <AdminDashboard /> : null;
};

export default Dashboard;
